// ---------------------------------------------------------------------------
// What the wizard sends to save and to finish a campaign (R17b)
//
// The bodies of POST /api/campaigns (the first save), PUT /api/campaigns/:id
// (every later one), and POST /api/campaign/prepare ("Finish setup"), and
// when the leads go along. Taken out of pages/campaign-wizard.ts and
// wizard/wiz-summary.ts unchanged; the page sends them. Plain TypeScript.
// ---------------------------------------------------------------------------

import type { CampaignDraft, ChannelFallback } from '../types.js';
import { deriveCampaignType } from '../types.js';
import { formatTimeHHmm } from './draft.js';

/** The per-rep buckets as the API takes them: one entry per rep. */
const emailBuckets = (draft: CampaignDraft) =>
  Object.entries(draft.emailAccountsByAie).map(([aiEmployeeId, emailAccounts]) => ({ aiEmployeeId, emailAccounts }));
const linkedinBuckets = (draft: CampaignDraft) =>
  Object.entries(draft.linkedinAccountIdsByAie).map(([aiEmployeeId, linkedinAccountIds]) => ({
    aiEmployeeId,
    linkedinAccountIds,
  }));

/**
 * The schedule with its send-window times as canonical wall-clock "HH:mm"
 * strings. The raw Date would JSON-encode to a UTC ISO string (baking in the
 * browser's offset), which the backend then misreads with server-local time —
 * breaking DST correctness for zones like Europe/Berlin.
 */
const scheduleForApi = (draft: CampaignDraft) => ({
  ...draft.schedule,
  startTime: formatTimeHHmm(draft.schedule.startTime),
  endTime: formatTimeHHmm(draft.schedule.endTime),
});

/**
 * Past 'draft', the whole Sales Rep block — who is assigned, at what share,
 * and which sending accounts each of them uses — is owned by the dedicated
 * /sales-reps endpoint (it re-keys leads, regenerates messages and validates
 * channel coverage in one atomic write). Autosaving any part of it here would
 * desync assignments from the actual leads, and worse: the account buckets are
 * pruned locally the moment a rep is unchecked, so an autosave landing between
 * "unchecked rep A" and "applied rep B" would silently wipe A's accounts
 * server-side while A is still the live assignment — a campaign that then
 * launches with no mailbox to send from.
 */
export const salesRepBlockOwnedByApply = (draft: CampaignDraft): boolean => !!draft.status && draft.status !== 'draft';

/**
 * PUT /api/campaigns/:id. The campaign type is always the one the sequence
 * makes (there is no picker for it any more).
 */
export function campaignUpdateBody(draft: CampaignDraft): Record<string, unknown> {
  return {
    name: draft.name || undefined,
    campaignType: deriveCampaignType(draft.sequence),
    offering: draft.offering,
    schedule: scheduleForApi(draft),
    sequence: draft.sequence,
    llmModel: draft.llmModel,
    ...(salesRepBlockOwnedByApply(draft)
      ? {}
      : {
          aiEmployeeId: draft.aiEmployeeId,
          aiEmployeeAssignments: draft.aiEmployeeAssignments,
          emailAccounts: draft.emailAccounts,
          linkedinAccountIds: draft.linkedinAccountIds,
          emailAccountsByAie: emailBuckets(draft),
          linkedinAccountIdsByAie: linkedinBuckets(draft),
        }),
    autoReply: draft.autoReply,
    replyPrompt: draft.replyPrompt,
    replyLlmModel: draft.replyLlmModel,
    crmAutoSync: draft.crmAutoSync,
    language: draft.language || undefined,
    apolloSearchInput: draft.apolloSearchInput,
    projectId: draft.projectId,
  };
}

/**
 * Whether an update sends the leads too (POST /api/campaigns/:id/leads). Once
 * the campaign has left 'draft' (generating / ready_to_launch /
 * preparing_to_launch / active / …), leads and messages carry real progress —
 * the leads endpoint destructively replaces every lead (fresh _ids, status
 * reset to 'pending'), which would wipe that progress and orphan every job and
 * message referencing the old lead ids. The server refuses this too (409,
 * gated on status, not just externalCampaignId); the call is skipped entirely
 * so opening a non-draft campaign in the editor can't silently corrupt it.
 * `status === undefined` covers a brand-new draft that was just created by the
 * same save and hasn't been re-fetched yet — always safe.
 */
export const leadsGoWithUpdate = (draft: CampaignDraft): boolean =>
  draft.leads.length > 0 && !draft.externalCampaignId && (draft.status === undefined || draft.status === 'draft');

/** The name a campaign is created with when it has none: "Draft - <offering> - <YYYY-MM-DD>". */
export const unnamedDraftName = (draft: CampaignDraft, now: Date): string =>
  `Draft - ${draft.offering.name || 'Untitled'} - ${now.toISOString().slice(0, 10)}`;

/** POST /api/campaigns: the first save of a new campaign. Its leads follow when it has any. */
export function campaignCreateBody(draft: CampaignDraft, now: Date): Record<string, unknown> {
  return {
    name: draft.name || unnamedDraftName(draft, now),
    campaignType: deriveCampaignType(draft.sequence),
    offering: draft.offering,
    schedule: scheduleForApi(draft),
    sequence: draft.sequence,
    emailAccounts: draft.emailAccounts,
    linkedinAccountIds: draft.linkedinAccountIds,
    llmModel: draft.llmModel,
    aiEmployeeId: draft.aiEmployeeId,
    aiEmployeeAssignments: draft.aiEmployeeAssignments,
    emailAccountsByAie: emailBuckets(draft),
    linkedinAccountIdsByAie: linkedinBuckets(draft),
    autoReply: draft.autoReply,
    replyPrompt: draft.replyPrompt,
    replyLlmModel: draft.replyLlmModel,
    crmAutoSync: draft.crmAutoSync,
    language: draft.language || undefined,
    apolloSearchInput: draft.apolloSearchInput,
    projectId: draft.projectId,
  };
}

/** "HH:mm" from a Date, using its local hours and minutes (the Summary's own; it takes a Date only). */
function wallClock(d: Date): string {
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * POST /api/campaign/prepare, "Finish setup": the draft as the server prepares
 * it, with the fallbacks picked for the coverage gaps. The wizard prepares, it
 * does not launch; the campaign page launches.
 */
export function prepareBody(
  draft: CampaignDraft,
  channelFallbacks: ChannelFallback[],
): { draft: Record<string, unknown> } {
  return {
    draft: {
      _id: draft._id,
      campaignName: draft.name,
      campaignType: draft.campaignType,
      offering: draft.offering,
      leads: draft.leads,
      schedule: {
        timezone: draft.schedule.timezone,
        startTime: wallClock(draft.schedule.startTime),
        endTime: wallClock(draft.schedule.endTime),
        sendDays: draft.schedule.sendDays,
        dailyLimit: draft.schedule.dailyLimit,
        autoDmOnAccept: draft.schedule.autoDmOnAccept ?? true,
      },
      sequence: draft.sequence,
      emailAccounts: draft.emailAccounts,
      linkedinAccountIds: draft.linkedinAccountIds,
      aiEmployeeId: draft.aiEmployeeId,
      aiEmployeeAssignments: draft.aiEmployeeAssignments,
      emailAccountsByAie: emailBuckets(draft),
      linkedinAccountIdsByAie: linkedinBuckets(draft),
      channelFallbacks,
      autoReply: draft.autoReply ?? false,
      replyPrompt: draft.replyPrompt || undefined,
      crmAutoSync: draft.crmAutoSync ?? false,
      language: draft.language || undefined,
      apolloSearchInput: draft.apolloSearchInput,
      projectId: draft.projectId,
    },
  };
}
